import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { useNavigate, useParams } from 'react-router-dom';
import { Heart, ArrowLeft, Calendar, Check, MessageCircle } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

// Helper to check if a phrase is a question
const isQuestion = (phrase) => {
  if (!phrase) return false;
  const trimmed = phrase.trim();
  return trimmed.endsWith('?') || 
    /^(what|where|when|who|why|how|can|do|does|is|are|will|would|could|should)\s/i.test(trimmed);
};

const adaptLanguageForStage = (examples, stage) => {
  if (!examples || examples.length === 0) return [];
  
  // Filter out questions for stages 1-4
  const filteredExamples = stage <= 4 
    ? examples.filter(ex => !isQuestion(ex))
    : examples;
  
  if (stage === 1) {
    return filteredExamples.filter(ex => ex.split(' ').length >= 2);
  } else if (stage === 2) {
    const baseGestalts = filteredExamples.filter(ex => ex.split(' ').length >= 2);
    const mitigated = [];
    for (let gestalt of baseGestalts.slice(0, 2)) {
      const words = gestalt.split(' ');
      if (words.length >= 2) {
        mitigated.push(`${words[0]} ${words[1]}...`);
      }
    }
    return [...baseGestalts.slice(0, 2), ...mitigated];
  } else if (stage === 3) {
    const singleWords = new Set();
    filteredExamples.forEach(ex => {
      const words = ex.replace(/[!,]/g, '').split(' ');
      words.forEach(w => {
        if (w.length > 2) singleWords.add(w.toLowerCase());
      });
    });
    return Array.from(singleWords).slice(0, 5);
  } else if (stage === 4) {
    const twoWord = new Set();
    filteredExamples.forEach(ex => {
      const words = ex.replace(/[!,]/g, '').split(' ');
      if (words.length >= 2) {
        twoWord.add(`${words[0]} ${words[1]}`);
      }
    });
    return Array.from(twoWord).slice(0, 5);
  } else {
    return examples;
  }
};

const getStageTip = (stage) => {
  const tips = {
    1: "Repeat these complete phrases consistently during activities. Your child is learning language in whole chunks.",
    2: "Model full phrases and celebrate when your child mixes parts from different gestalts. This shows language processing!",
    3: "Emphasize these single words clearly. Point to objects and name them. Single words are emerging from gestalts.",
    4: "Model these 2-word combinations naturally. Your child is starting to create their own novel phrases!",
    5: "Use these phrases with proper grammar. Expand on your child's attempts by adding one word.",
    6: "Model complex sentences and support storytelling. Your child can handle advanced language structures."
  };
  return tips[stage] || '';
};

export default function ParentWeeklyFocus() {
  const navigate = useNavigate();
  const { childId } = useParams();
  const [focus, setFocus] = useState(null);
  const [child, setChild] = useState(null);
  const [loading, setLoading] = useState(true);
  const [allActivities, setAllActivities] = useState([]);
  const [completions, setCompletions] = useState({});
  const [commentDialog, setCommentDialog] = useState({ open: false, activityId: null, activityName: '' });
  const [currentComment, setCurrentComment] = useState('');
  const [customizedActivities, setCustomizedActivities] = useState({});

  useEffect(() => {
    fetchData();
  }, [childId]);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Fetch child data
      const childRes = await axios.get(`${API}/children/${childId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setChild(childRes.data);

      // Fetch weekly focus
      try {
        const focusRes = await axios.get(`${API}/weekly-focus/${childId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setFocus(focusRes.data);
        
        // Fetch activities if focus has activity_ids
        if (focusRes.data.activity_ids?.length > 0) {
          const activitiesRes = await axios.get(`${API}/activities`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setAllActivities(activitiesRes.data);
          
          // Fetch customizations
          try {
            const customRes = await axios.get(`${API}/activities/customized/${childId}`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            const customMap = {};
            customRes.data.forEach(c => {
              customMap[c.original_activity_id] = c;
            });
            setCustomizedActivities(customMap);
          } catch (err) {
            console.error('Failed to load customizations');
          }
          
          // Fetch completions
          const completionsRes = await axios.get(
            `${API}/activity-completions/${childId}/${focusRes.data.id}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          
          // Convert to a map for easy lookup
          const completionMap = {};
          completionsRes.data.forEach(c => {
            completionMap[c.activity_id] = c;
          });
          setCompletions(completionMap);
        }
      } catch (error) {
        setFocus(null);
      }
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Helper to get activity with customizations applied
  const getActivityForChild = (activity) => {
    const customized = customizedActivities[activity.id];
    if (customized) {
      return {
        ...activity,
        name: customized.name,
        language_examples: customized.language_examples,
        follow_lead_guidance: customized.follow_lead_guidance,
        notes: customized.notes,
        isCustomized: true
      };
    }
    return { ...activity, isCustomized: false };
  };

  const handleToggleComplete = async (activityId) => {
    try {
      const token = localStorage.getItem('token');
      const currentCompletion = completions[activityId];
      const newCompletedState = !currentCompletion?.completed;
      
      await axios.post(`${API}/activity-completion`, {
        child_id: childId,
        weekly_focus_id: focus.id,
        activity_id: activityId,
        completed: newCompletedState,
        parent_comment: currentCompletion?.parent_comment || ''
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setCompletions(prev => ({
        ...prev,
        [activityId]: {
          ...prev[activityId],
          completed: newCompletedState,
          activity_id: activityId
        }
      }));
      
      if (newCompletedState) {
        toast.success('Activity completed! Great job!');
      }
    } catch (error) {
      toast.error('Failed to update activity');
    }
  };

  const openCommentDialog = (activityId, activityName) => {
    setCurrentComment(completions[activityId]?.parent_comment || '');
    setCommentDialog({ open: true, activityId, activityName });
  };

  const handleSaveComment = async () => {
    try {
      const token = localStorage.getItem('token');
      const activityId = commentDialog.activityId;
      const currentCompletion = completions[activityId];
      
      await axios.post(`${API}/activity-completion`, {
        child_id: childId,
        weekly_focus_id: focus.id,
        activity_id: activityId,
        completed: currentCompletion?.completed || false,
        parent_comment: currentComment
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setCompletions(prev => ({
        ...prev,
        [activityId]: {
          ...prev[activityId],
          parent_comment: currentComment,
          activity_id: activityId
        }
      }));
      
      setCommentDialog({ open: false, activityId: null, activityName: '' });
      toast.success('Comment saved!');
    } catch (error) {
      toast.error('Failed to save comment');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <p className="text-stone-600">Loading...</p>
      </div>
    );
  }

  const completedCount = Object.values(completions).filter(c => c.completed).length;
  const totalActivities = focus?.activity_ids?.length || 0;

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <nav className="bg-white border-b border-stone-100 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              data-testid="back-btn"
              onClick={() => navigate(`/children/${childId}`)}
              className="rounded-full"
            >
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                <Heart className="w-5 h-5 text-primary" />
              </div>
              <h1 className="text-2xl font-serif text-primary-900">PlayFirst</h1>
            </div>
          </div>
        </div>
      </nav>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="mb-8">
            <h2 className="text-4xl md:text-5xl font-serif font-normal text-primary-900 mb-2">
              This week&apos;s focus
            </h2>
            <p className="text-lg text-stone-600">{child?.name}</p>
          </div>

          {!focus ? (
            <Card className="p-12 text-center bg-white rounded-3xl border border-stone-100" data-testid="empty-state">
              <Calendar className="w-16 h-16 mx-auto mb-4 text-primary/50" />
              <h3 className="text-xl font-medium text-stone-700 mb-2">No weekly focus yet</h3>
              <p className="text-stone-500">Your therapist will set this week&apos;s focus soon</p>
            </Card>
          ) : (
            <div className="space-y-6">
              <Card className="p-8 bg-white rounded-3xl border-2 border-primary/20 shadow-sm" data-testid="focus-card">
                <div className="flex items-center gap-3 mb-6">
                  <Calendar className="w-8 h-8 text-primary" />
                  <div>
                    <p className="text-xs font-bold tracking-wide uppercase text-stone-400">Week of</p>
                    <p className="text-lg text-stone-700">{new Date(focus.week_start_date).toLocaleDateString()}</p>
                  </div>
                </div>

                <div className="space-y-6">
                  <div>
                    <h3 className="text-2xl font-serif text-primary-900 mb-3">Focus</h3>
                    <p className="text-lg text-stone-700 leading-relaxed">{focus.focus_statement}</p>
                  </div>

                  {focus.suggested_activities.length > 0 && (
                    <div>
                      <h3 className="text-xl font-serif text-primary-900 mb-3">Activities to try</h3>
                      <ul className="space-y-2">
                        {focus.suggested_activities.map((activity, i) => (
                          <li key={i} className="flex items-start gap-3">
                            <span className="w-6 h-6 rounded-full bg-secondary/20 flex items-center justify-center text-secondary text-sm font-medium mt-0.5">
                              {i + 1}
                            </span>
                            <span className="text-base text-stone-700 flex-1">{activity}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Activity tracking section for parents */}
                  {focus.activity_ids && focus.activity_ids.length > 0 && (
                    <div className="mt-8 pt-6 border-t border-stone-200">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-xl font-serif text-primary-900">
                          Home Activities
                          {child?.glp_stage && (
                            <span className="ml-3 px-3 py-1 bg-purple-100 text-purple-700 text-xs rounded-full font-normal">
                              Stage {child.glp_stage} adapted
                            </span>
                          )}
                        </h3>
                        {totalActivities > 0 && (
                          <div className="flex items-center gap-2 px-3 py-1 bg-green-50 rounded-full">
                            <Check className="w-4 h-4 text-green-600" />
                            <span className="text-sm font-medium text-green-700">
                              {completedCount} of {totalActivities} done
                            </span>
                          </div>
                        )}
                      </div>
                      
                      <div className="space-y-4">
                        {allActivities
                          .filter(activity => focus.activity_ids.includes(activity.id))
                          .map((activity, index) => {
                            const displayActivity = getActivityForChild(activity);
                            const completion = completions[activity.id] || {};
                            const isCompleted = completion.completed;
                            
                            return (
                              <div 
                                key={activity.id} 
                                className={`p-5 rounded-2xl border-2 transition-all ${
                                  isCompleted 
                                    ? 'bg-green-50 border-green-200' 
                                    : displayActivity.isCustomized 
                                      ? 'bg-gradient-to-br from-purple-50 to-purple-100/50 border-purple-200'
                                      : 'bg-gradient-to-br from-secondary/5 to-secondary/10 border-secondary/20'
                                }`}
                                data-testid={`activity-card-${activity.id}`}
                              >
                                <div className="flex items-start gap-4">
                                  <div className="pt-1">
                                    <Checkbox
                                      checked={isCompleted}
                                      onCheckedChange={() => handleToggleComplete(activity.id)}
                                      data-testid={`activity-checkbox-${activity.id}`}
                                      className="w-6 h-6"
                                    />
                                  </div>
                                  
                                  <div className="flex-1">
                                    <div className="flex items-start justify-between gap-3 mb-3">
                                      <div>
                                        <div className="flex items-center gap-2 flex-wrap">
                                          <h4 className={`text-lg font-medium ${isCompleted ? 'text-green-800 line-through' : 'text-primary-900'}`}>
                                            {displayActivity.name}
                                          </h4>
                                          {displayActivity.isCustomized && (
                                            <span className="px-2 py-0.5 bg-purple-200 text-purple-700 text-xs rounded-full">
                                              Personalized
                                            </span>
                                          )}
                                        </div>
                                        <p className="text-sm text-stone-600">{activity.play_type}</p>
                                      </div>
                                      
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => openCommentDialog(activity.id, displayActivity.name)}
                                        data-testid={`comment-btn-${activity.id}`}
                                        className="rounded-full"
                                      >
                                        <MessageCircle className={`w-4 h-4 ${completion.parent_comment ? 'text-primary fill-primary/20' : 'text-stone-400'}`} />
                                      </Button>
                                    </div>
                                    
                                    {displayActivity.language_examples && displayActivity.language_examples.length > 0 && (
                                      <div className="mt-3 p-3 bg-white rounded-xl">
                                        <p className="text-xs font-bold tracking-wide uppercase text-stone-400 mb-2">Language to model:</p>
                                        <div className="flex flex-wrap gap-2">
                                          {(child?.glp_stage 
                                            ? adaptLanguageForStage(displayActivity.language_examples, child.glp_stage)
                                            : displayActivity.language_examples
                                          ).slice(0, 4).map((phrase, i) => (
                                            <span key={i} className="px-3 py-1 bg-primary/10 text-primary rounded-full text-sm">
                                              &quot;{phrase}&quot;
                                            </span>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                    
                                    {displayActivity.follow_lead_guidance && (
                                      <div className="mt-3 p-3 bg-blue-50 rounded-xl">
                                        <p className="text-xs font-bold text-blue-900 mb-1">Follow their lead:</p>
                                        <p className="text-sm text-blue-800">{displayActivity.follow_lead_guidance}</p>
                                      </div>
                                    )}
                                    
                                    {completion.parent_comment && (
                                      <div className="mt-3 p-3 bg-amber-50 rounded-xl">
                                        <p className="text-xs font-bold text-amber-900 mb-1">Your notes:</p>
                                        <p className="text-sm text-amber-800">{completion.parent_comment}</p>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                      </div>
                    </div>
                  )}

                  {focus.modeled_language.length > 0 && (
                    <div>
                      <h3 className="text-xl font-serif text-primary-900 mb-3">
                        Language to model
                        {child?.glp_stage && (
                          <span className="ml-3 px-3 py-1 bg-purple-100 text-purple-700 text-xs rounded-full font-normal">
                            Stage {child.glp_stage} adapted
                          </span>
                        )}
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {(child?.glp_stage 
                          ? adaptLanguageForStage(focus.modeled_language, child.glp_stage)
                          : focus.modeled_language
                        ).map((phrase, i) => (
                          <div key={i} className="p-4 bg-primary/5 rounded-2xl">
                            <p className="text-base text-primary-900 font-medium">&quot;{phrase}&quot;</p>
                          </div>
                        ))}
                      </div>
                      {child?.glp_stage && (
                        <div className="mt-4 p-4 bg-purple-50 rounded-2xl">
                          <p className="text-sm text-purple-900">
                            <strong>Stage {child.glp_stage} Guidance:</strong> {getStageTip(child.glp_stage)}
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </Card>

              <Card className="p-8 bg-gradient-to-br from-primary/5 to-secondary/5 rounded-3xl border border-primary/10">
                <p className="font-accent text-2xl text-center text-primary-800 leading-relaxed -rotate-1">
                  {focus.reassurance_message}
                </p>
              </Card>
            </div>
          )}
        </motion.div>
      </main>

      {/* Comment Dialog */}
      <Dialog open={commentDialog.open} onOpenChange={(open) => setCommentDialog({ ...commentDialog, open })}>
        <DialogContent className="rounded-3xl" data-testid="comment-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-serif">
              Add a note for &quot;{commentDialog.activityName}&quot;
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <p className="text-sm text-stone-600">
              Share how the activity went, what your child enjoyed, or any observations. Your therapist will see this.
            </p>
            <Textarea
              placeholder="e.g., He loved the bubbles! Used 'more' spontaneously twice."
              value={currentComment}
              onChange={(e) => setCurrentComment(e.target.value)}
              data-testid="comment-textarea"
              className="rounded-2xl min-h-32"
            />
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setCommentDialog({ open: false, activityId: null, activityName: '' })}
                className="flex-1 rounded-full"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveComment}
                data-testid="save-comment-btn"
                className="flex-1 rounded-full"
              >
                Save Note
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
